/**
 * 74.15: the mountains stand out of the haze (aerial perspective). The haze (rendering/skyHaze.ts) is a linear fog from half
 * a horizon to four, the horizon from the eye's height — right for the plain (ground past the eye's horizon is under the
 * curve anyway), wrong for what stands over it: on a megaplanet a range at 420 people, its summits 120 people up and in
 * sight to ~900, drew as the haze's colour (lane E's 388 shots). Here a ground fragment's fog distance is less its own
 * horizon: max(0, d − √(2R·y)), y its height over the eye's ground. The plain (y ≈ 0) fogs as before; a range's foot sits in
 * the haze and its flanks come out of it.
 *
 * `uHazeLiftOn` weighs it: 0 under the weather's sight band (the person's view keeps the sim's sight), eased back to 1 with
 * the commander's view; in space it is 0. Materials it isn't applied to (props, people) keep three's own fog.
 */
import * as THREE from 'three';

/** Shared by every ground material (skyHaze.ts sets them each frame): the planet's centre, the eye's ground radius, R. */
export const HAZE_LIFT = {
  uHazeCentre: { value: new THREE.Vector3() },
  uHazeGround: { value: 0 },
  uHazeR: { value: 0 },
  uHazeLiftOn: { value: 0 },
};

/** The fog distance a ground point at height `y` over the eye's ground, `d` away, takes (world units): the shader's rule. */
export function hazeLiftDepth(d: number, y: number, R: number): number {
  return Math.max(0, d - Math.sqrt(2 * R * Math.max(0, y)));
}

/** The fog chunk on a ground material: three's linear fog over the lifted distance (its FogExp2 path untouched). */
export function applyHazeLift(material: THREE.Material): void {
  const before = material.onBeforeCompile?.bind(material);
  material.onBeforeCompile = (shader, renderer) => {
    before?.(shader, renderer);
    Object.assign(shader.uniforms, HAZE_LIFT);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vHazeWorld;')
      .replace('#include <fog_vertex>', '#include <fog_vertex>\n  vHazeWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vHazeWorld;\nuniform vec3 uHazeCentre;\nuniform float uHazeGround;\nuniform float uHazeR;\nuniform float uHazeLiftOn;')
      .replace('#include <fog_fragment>', `#if defined( USE_FOG ) && !defined( FOG_EXP2 )
  float hazeY = max(0.0, length(vHazeWorld - uHazeCentre) - uHazeGround);
  float hazeD = max(0.0, vFogDepth - uHazeLiftOn * sqrt(2.0 * uHazeR * hazeY));
  gl_FragColor.rgb = mix(gl_FragColor.rgb, fogColor, smoothstep(fogNear, fogFar, hazeD));
#else
  #include <fog_fragment>
#endif`);
  };
  const key = material.customProgramCacheKey?.bind(material);
  material.customProgramCacheKey = () => `${key ? key() : ''}|haze-lift`;
  material.needsUpdate = true;
}
